import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import React, { useEffect, useRef, useState } from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ConsoleCard from '../MainPageLayout/ConsoleCard';
import MenuBoardTimeTile from './MenuBoardTimeTile';
import ResponseTimeTile from './ResponseTimeTile';
import TurnTile from './TurnTile';
import SentimentTile from './SentimentTile';
import PulseSignals from './PulseSignals';
import PulseAdvise from './PulseAdvise';
import { useAppSelector } from '../../app/hooks';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';
import useAIStatusBand from './useAIStatusBand.hooks';

/**
 * CUSTOMER PULSE (left pane, top card).
 *
 * A collapsible card that gives the human a 2-second read of the live order's AI
 * status. The header (icon + title + collapse chevron) sits inside the white card
 * (matching Action Center); a full-bleed status band pinned at the card's bottom
 * edge encodes the LIVE AI status — green/yellow/red soft tints, plus a prominent
 * vivid blink on takeover.
 *
 * Under the header the card is a compact strip: the Menu Board Time + Response
 * time meters, the live signal + reminder chips (PulseSignals), and the folded-in
 * AI co-pilot ask (PulseAdvise) — with the AI status band pinned at the bottom.
 *
 * HITL-1663 (hitl_7_0) replaces all of that chrome with a bare chip strip. The
 * card frame, the "AI COPILOT" header and its chevron all go, as do the Menu
 * Board Time, Turn and Sentiment tiles — the countdown moved to the header in
 * HITL-1662 and the other two were read by nobody. What is left is Response
 * time as one dial chip, the signal chips, and the AI ask, flowing in a single
 * wrapping row that scrolls rather than clips when the agent drags it short.
 * The status band already left for full console width in HITL-1660, so the 7.0
 * strip has no footer either. Everything the flag-off path renders is untouched.
 */

// ── HITL-1663: the 7.0 strip ────────────────────────────────────────────────
// Inline styles rather than makeStyles rules on purpose. Adding rules to this
// sheet advances JSS's GLOBAL class-name counter, which renumbers generated
// names in unrelated components and moves their snapshots — for a change that
// nobody with the flag off can see. Inline `style` costs nothing here: two
// static objects, hoisted so they are not rebuilt per render.
//
// The strip IS the flex-wrap container (rather than wrapping one inside it) so
// that `childElementCount === 0` is an exact test for "nothing to show" — which
// is what drives the auto-collapse below. Every child self-gates to null, so an
// empty strip really does have no children.
const STRIP_STYLE: React.CSSProperties = {
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexWrap: 'wrap',
  // Rows stack from the top; chips centre against each other within a row.
  alignContent: 'flex-start',
  alignItems: 'center',
  gap: '6px',
  padding: HITL3_PADDING,
  backgroundColor: Colors.white,
  // Drag the strip shorter than its content and it scrolls. Never clips, and
  // never scrolls sideways — chips wrap instead.
  overflowY: 'auto',
  overflowX: 'hidden',
};

const useStyles = makeStyles((theme: Theme) => ({
  // AI status band (folded-in AIStatusBar): a full-bleed strip pinned at the
  // card's bottom edge (marginTop:auto pushes it down in the flex column).
  // Background encodes the LIVE AI status and is applied inline via the shared
  // getStatusBarColor() so it matches the legacy AIStatusBar exactly (vivid
  // green/yellow/red), plus a prominent vivid blink for a takeover
  // (bandTakeover). The card padding is 0, so the band already spans edge-to-edge
  // (no negative margins needed); the shared 8px inset pads its text on all sides.
  statusBand: {
    flexShrink: 0,
    marginTop: 'auto',
    padding: HITL3_PADDING,
    // Text styled like the "Continue" intent pill: medium weight, shared hitl3 size.
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    // Left-aligned text, vertically centered within the band's available height.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  // One line: truncate with an ellipsis (no wrap, no h-scroll). minWidth:0 lets
  // this flex child shrink so the ellipsis engages; the full message shows on
  // hover via the title attribute on the band.
  statusText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Takeover: a prominent vivid red throb (light↔dark) with white text — a
  // "must not miss" alert, so intentionally NOT a soft tint. Static $keyframes
  // reference (JSS resolves the scoped name only in a static rule).
  '@keyframes statusTakeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' },
    '50%': { backgroundColor: '#990000' },
  },
  bandTakeover: {
    color: Colors.white,
    animation: '$statusTakeoverPulse 1s ease-in-out infinite',
  },
  // HITL 3.0 metric tiles row, laid out side-by-side under the header. Each tile
  // self-gates to null, so the row can be empty until one becomes active.
  // flex-start (not center) tops-align the tiles so every tile's value + label
  // sit on the same line — tiles with a progress bar (Menu Board Time, Response
  // time) are taller than those without (Turn, Sentiment), and centering would
  // otherwise push the shorter tiles' text down.
  metricRow: {
    marginTop: HITL3_PADDING,
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface CustomerPulseProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CustomerPulse = ({
  open: openProp,
  onOpenChange,
}: CustomerPulseProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // ── AI status band ──────────────────────────────────────────────────────────
  // The precedence ladder, the pure-HITL gate and the colours moved to
  // useAIStatusBand (HITL-1660) so this folded-in 3.0 footer and the full-width
  // hitl_7_0 strip share one source of truth rather than drifting apart.
  const {
    show: showStatusBand,
    message: statusMessage,
    background: bandBackground,
    textColor: bandTextColor,
    blink: showTakeoverBlink,
  } = useAIStatusBand();

  // hitl_7_0 lifts the band out to full console width (see AIStatusBand). The
  // card then renders no footer at all, so the band never shows in two places.
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);

  // Still read here on its own: it also blanks the card BODY below, which is a
  // separate concern from what the band says.
  const { isStaffInterventionHappening } = useIntervention();

  // ── Auto-collapse an empty strip (7.0) ────────────────────────────────────
  // The point of the ticket is that the space the Copilot card wasted goes back
  // to the conversation, so a strip with nothing in it must not hold a quarter
  // of the column. Runs after every render with no dependency array on purpose:
  // emptiness is a property of children this component does not own, so there
  // is nothing to list. It is one property read and a compare.
  //
  // Only TRANSITIONS act. If the agent drags an empty strip back open, the
  // effect sees no transition and leaves it alone rather than slamming it shut
  // again — collapse is a default, not a lock.
  const stripRef = useRef<HTMLDivElement>(null);
  const wasEmptyRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isHitl7) return;
    const el = stripRef.current;
    if (!el) return;
    const empty = el.childElementCount === 0;
    if (wasEmptyRef.current === empty) return;
    wasEmptyRef.current = empty;
    onOpenChange?.(!empty);
  });

  if (isHitl7) {
    return (
      <div ref={stripRef} style={STRIP_STYLE} data-testid="customer-pulse">
        {/* Blanked during staff intervention for the same reason as the 3.0
            card below: every chip describes an AI-run order that stopped
            applying the moment a human took it over. The strip then reports
            empty and collapses, which is the right answer — the full-width
            status band is already saying "Staff Intervention". */}
        {!isStaffInterventionHappening && (
          <>
            <ResponseTimeTile />
            <PulseSignals />
            {/* HITL-1668: the ask is NOT here under 7.0 — it renders as the
                last child of the transcript instead (LiveTranscriptCard). Its
                leaving is what stops this strip reflowing every time a
                suggestion arrives or is answered: the strip auto-collapses on
                childElementCount, so an element appearing and vanishing inside
                it was resizing the whole left column mid-order. */}
          </>
        )}
      </div>
    );
  }

  return (
    <ConsoleCard
      icon={<AutoAwesomeOutlinedIcon />}
      title="AI Copilot"
      open={open}
      onToggle={toggleOpen}
      testId="customer-pulse"
      footer={
        // Band renders only for AI restaurants (showStatusBand). When shown it is
        // ALWAYS present — the live status / takeover blink must stay visible even
        // when the card is collapsed to a strip; marginTop:auto pins it to the
        // bottom edge. Pure HITL → no band (undefined); the omnibar red border is
        // the staff-intervention cue there.
        //
        // Under hitl_7_0 the band has moved to full console width (AIStatusBand),
        // so the card gives up its footer entirely and the space goes back to the
        // Pulse strip.
        showStatusBand && !isHitl7 ? (
          <div
            data-testid="ai-status-band"
            data-blink={showTakeoverBlink}
            className={`${classes.statusBand}${
              showTakeoverBlink ? ` ${classes.bandTakeover}` : ''
            }`}
            style={
              showTakeoverBlink
                ? undefined
                : { backgroundColor: bandBackground, color: bandTextColor }
            }
            title={statusMessage}
          >
            <span className={classes.statusText}>{statusMessage}</span>
          </div>
        ) : undefined
      }
    >
      {/* HITL 3.0 Pulse strip: the metric tiles (side by side) → the signal +
          reminder chips → the folded-in AI ask. The whole console is already
          gated on hitl_3_0 at the mount site (Body), so no local flag branch is
          needed. Each tile self-gates:
            • Menu Board Time — while a session is active; the car's dwell gauge.
            • Turn — active session + AI enabled; the ai_session_state turn count
              (no progress bar).
            • Sentiment — active session + AI enabled; the ai_session_state
              sentiment (colour + face, muted "—" default).
            • Response time — active session + (agent intercepted OR pure HITL);
              the per-turn reply SLA.
          PulseSignals / PulseAdvise self-gate to null when there's nothing to
          surface.

          During staff intervention the whole body is blanked — the metrics,
          signals and AI ask all describe an AI-run order that no longer applies
          once a human takes over. Only the AI status band (footer) stays, now
          reading "Staff Intervention". */}
      {!isStaffInterventionHappening && (
        <>
          <div className={classes.metricRow}>
            <MenuBoardTimeTile />
            <TurnTile />
            <SentimentTile />
            <ResponseTimeTile />
          </div>
          <PulseSignals />
          <PulseAdvise />
        </>
      )}
    </ConsoleCard>
  );
};

export default CustomerPulse;
